<template>
  <component
    :is="component.component.name"
    v-for="(component, index) in specificComponents"
    :key="`dynamicComponent-${index}`"
    :component="component"
  ></component>
</template>

<script lang="ts">
// layouts
import FooterOne from '~/components/theme7/base/footer/FooterOne.vue';
import HeaderOne from '~/components/theme7/base/header/HeaderOne.vue';

// library components
import FormContainer from '~/components/library/FormContainer.vue';

// theme7 components
import KnowledgeHubPage from '~/components/theme7/components/KnowledgeHubPage.vue';
import Test from '~/components/theme7/components/Test.vue';
import ScrollAnimationTexts from '~/components/theme7/components/ScrollAnimationTexts.vue';
import OurValuations from '~/components/theme7/components/OurValuations.vue';
import OurTeam from '~/components/theme7/components/OurTeam.vue';
import OurSites from '~/components/theme7/components/OurSites.vue';
import Properties from '~/components/theme7/properties/Properties.vue';
import Calendly from '~/components/theme7/components/Calendly.vue';
import LatestPodcast from '~/components/theme7/components/LatestPodcast.vue';
import RegisterEntry from '~/components/theme7/register/RegisterEntry.vue';
import Register from '~/components/theme7/register/Register.vue';
import Podcasts from '~/components/theme7/components/Podcasts.vue';
import StoryBox from '~/components/theme7/components/StoryBox.vue';
import TakeAction from '~/components/theme7/components/TakeAction.vue';
import OurValues from '~/components/theme7/components/OurValues.vue';
import ContentBox from '~/components/theme7/components/ContentBox.vue';
import GoogleReview from '~/components/theme7/components/GoogleReview.vue';
import HeroSection from '~/components/theme7/components/HeroSection.vue';
import PropertyListItem from '~/components/theme7/properties/PropertyListItem.vue';
import ContactForm from '~/components/theme7/components/ContactForm.vue';
import VideoBox from '~/components/theme7/components/VideoBox.vue';
import StorySlider from '~/components/theme7/components/StorySlider.vue';
import OurGuide from '~/components/theme7/components/OurGuide.vue';
import InfoBox from '~/components/theme7/components/InfoBox.vue';
import IntroSection from '~/components/theme7/components/IntroSection.vue';
import WhyBox from '~/components/theme7/components/WhyBox.vue';
import InfoBanner from '~/components/theme7/components/InfoBanner.vue';
import BlogsBox from '~/components/theme7/components/BlogsBox.vue';
import AgentCard from '~/components/theme7/components/AgentCard.vue';
import AreaBox from '~/components/theme7/components/AreaBox.vue';
import AreaInfo from '~/components/theme7/components/AreaInfo.vue';
import SingleStory from '~/components/theme7/components/SingleStory.vue';
import WhySection from '~/components/theme7/components/WhySection.vue';
import BookingBox from '~/components/theme7/components/BookingBox.vue';
import BranchMap from '~/components/theme7/components/BranchMap.vue';
import ContactUs from '~/components/theme7/components/ContactUs.vue';
import BrandBanner from '~/components/theme7/components/BrandBanner.vue';
import FeatureBox from '~/components/theme7/components/FeatureBox.vue';
import CountSection from '~/components/theme7/components/CountSection.vue';
import SingleVideo from '~/components/theme7/components/SingleVideo.vue';
import VideoTestimonials from '~/components/theme7/components/VideoTestimonials.vue';
import BranchMapAndContactForm from '~/components/theme7/components/BranchMapAndContactForm.vue';
import OurOffice from '~/components/theme7/components/OurOffice.vue';
import ValuationForm from '~/components/theme7/components/ValuationForm.vue';
import OurHistory from '~/components/theme7/components/OurHistory.vue';
import OurGallery from '~/components/theme7/components/OurGallery.vue';
import SocialGallery from '~/components/theme7/components/SocialGallery.vue';
import SelectedBlogsBox from '~/components/theme7/blogs/SelectedBlogsBox.vue';
import MergedValues from '~/components/theme7/components/MergedValues.vue';
import ProcessSlider from '~/components/theme7/components/ProcessSlider.vue';
import FeeCalculator from '~/components/theme7/components/FeeCalculator.vue';
import CaseStudies from '~/components/theme7/components/CaseStudies.vue';
import CaseStudySummary from '~/components/theme7/components/CaseStudySummary.vue';
import Feedback from '~/components/theme7/components/Feedback.vue';
import ManualBlogsBox from '~/components/theme7/components/ManualBlogsBox.vue';
import BlendedFeeCalculator from '~/components/common/BlendedFeeCalculator.vue';
import ComparisonCalculator from '~/components/common/ComparisonCalculator.vue';
// common
import PropertiesEntry from '~/components/common/properties/PropertiesEntry.vue';
import Separator from '~/components/library/Separator.vue';
import RedirectTo from '~/components/common/RedirectTo.vue';
import NotFoundPage from '~/components/theme7/components/NotFoundPage.vue';
import ManageCard from '~/components/library/ManageCard.vue';
// blogs components
import {
  LibraryPropertiesOnTheFlySearchBox,
  Theme7BlogsPage,
  Theme7BlogsOurBlog,
  Theme7PropertiesLatestProperties,
  Theme7PropertiesPropertyDetail,
  Theme7BlogsBlogDetail,
  Theme7ComponentsKnowledgeHubBox,
} from '#components';
import UpdateProfileEntry from '~/components/library/heads_up_alerts/UpdateProfileEntry.vue';
import LoginBox from '~/components/library/LoginBox.vue';

export default defineNuxtComponent({
  props: {
    specificComponents: {
      required: false,
      default: () => [],
    },
  },

  components: {
    HeaderOne,
    FooterOne,
    FormContainer,
    Calendly,
    Properties,
    ContentBox,
    GoogleReview,
    TakeAction,
    BlendedFeeCalculator,
    ComparisonCalculator,
    OurValues,
    Register,
    HeroSection,
    NotFoundPage,
    ManageCard,
    OurSites,
    RegisterEntry,
    InfoBanner,
    PropertiesEntry,
    Separator,
    RedirectTo,
    Podcasts,
    LatestPodcast,
    OnTheFlyPropertySearchBox: LibraryPropertiesOnTheFlySearchBox,
    PropertyDetail: Theme7PropertiesPropertyDetail,
    LatestProperties: Theme7PropertiesLatestProperties,
    OurBlog: Theme7BlogsOurBlog,
    BlogsPage: Theme7BlogsPage,
    BlogDetail: Theme7BlogsBlogDetail,
    UpdateProfileEntry,
    LoginBox,
    Test,
    ScrollAnimationTexts,
    SocialGallery,
    OurValuations,
    OurTeam,
    StoryBox,
    CountSection,
    ContactForm,
    OurGallery,
    VideoBox,
    StorySlider,
    OurGuide,
    InfoBox,
    BlogsBox,
    IntroSection,
    WhyBox,
    AgentCard,
    BrandBanner,
    KnowledgeHubBox: Theme7ComponentsKnowledgeHubBox,
    KnowledgeHubPage,
    AreaInfo,
    AreaBox,
    ValuationForm,
    SingleStory,
    FeatureBox,
    SingleVideo,
    ProcessSlider,
    BookingBox,
    BranchMapAndContactForm,
    OurHistory,
    WhySection,
    OurOffice,
    BranchMap,
    VideoTestimonials,
    ContactUs,
    SelectedBlogsBox,
    MergedValues,
    FeeCalculator,
    CaseStudies,
    CaseStudySummary,
    Feedback,
    ManualBlogsBox,
  },
});
</script>
